import * as Sentry from '@sentry/react';

// Track if Sentry has been initialized
let isInitialized = false;

/**
 * Initialize Sentry with the provided configuration
 * This ensures Sentry is only initialized once
 */
export const initializeSentry = (): void => {
  // Skip if already initialized or if DSN is not provided
  if (isInitialized || !import.meta.env.VITE_SENTRY_DSN) {
    return;
  }

  try {
    console.log('Initializing Sentry');
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: false,
          blockAllMedia: false,
        }),
        Sentry.feedbackIntegration({
          colorScheme: 'system',
          triggerLabel: 'Feedback',
          triggerAriaLabel: 'Feedback',
          submitButtonLabel: 'Send Feedback',
          messagePlaceholder: 'Did you find any bugs? Do you have any suggestions?',
          formTitle: 'Send Feedback',
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0, // Capture 100% of transactions in development, adjust in production
      // Session Replay
      replaysSessionSampleRate: 0.1, // Sample rate for session replays (10%)
      replaysOnErrorSampleRate: 1.0, // Sample rate for replays when errors occur (100%)
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    });

    isInitialized = true;
    console.log('Sentry initialized successfully');
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
};

// Export the Sentry instance for direct access when needed
export { Sentry };

/**
 * Capture an exception with Sentry
 * @param error The error to capture
 * @param context Additional context to add to the error
 */
export const captureException = (error: Error, context?: Record<string, any>): void => {
  Sentry.captureException(error, { contexts: context });
};
